import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

interface Props {
  title: string;
  text: string;
  url: string;
}

export function ShareButton({ title, text, url }: Props) {
  const { t } = useTranslation();
  const [toast, setToast] = useState(false);

  async function handleShare() {
    const absoluteUrl = url.startsWith('http')
      ? url
      : `${window.location.origin}${url}`;
    const payload = { title, text, url: absoluteUrl };

    if (typeof navigator.share === 'function') {
      try {
        await navigator.share(payload);
        return;
      } catch {
        // user cancelled or not available — fall through to clipboard
      }
    }

    try {
      await navigator.clipboard.writeText(absoluteUrl);
      setToast(true);
      setTimeout(() => setToast(false), 2000);
    } catch {
      // clipboard unavailable — do nothing (very rare)
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={handleShare}
        aria-label={t('quote.share')}
        className="flex items-center justify-center w-9 h-9 rounded-full bg-cream border border-oud-100/60 text-oud-700 hover:text-oud-900 hover:bg-oud-50 transition-colors"
      >
        <ShareIcon className="w-4 h-4" />
      </button>

      <AnimatePresence>
        {toast ? (
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed left-1/2 -translate-x-1/2 bottom-28 z-50 bg-oud-900 text-sand text-sm font-bold rounded-full px-4 py-2 shadow-quote"
          >
            {t('quote.share_copied')}
          </motion.div>
        ) : null}
      </AnimatePresence>
    </>
  );
}

function ShareIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4" />
    </svg>
  );
}
